import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { where, Unsubscribe } from 'firebase/firestore';

// Models
import { Contact, Conversation } from 'src/app/services/messaging/models';
import { createFirebaseSubjectListener, FirebaseSubjectListener } from 'src/app/models/firebase-listener/firebase-listener.model';

// Services
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { LoggingService } from 'src/app/services/logging/logging.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { UserService } from 'src/app/services/user/user.service';
import { OnlineStatusService } from '../online-status/online-status.service';
import { OnlineStatus } from 'src/app/models/online-status/online-status';

declare global {
  interface Window {
    contactService: any;
    cs: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ContactsService {

  public contacts = new BehaviorSubject<Contact[]>([]);
  public contactsLoaded: Promise<Contact[]>;

  private resolveContacts: (value: Contact[]) => void;

  private contactListener: FirebaseSubjectListener<Subject<Contact[]>>;
  private orgMemberListener: Unsubscribe;
  private personalContacts: Contact[] = [];
  private orgMemberContacts: Contact[] = [];

  constructor(
    private firebase: FirebaseService,
    private logging: LoggingService,
    private onlineStatus: OnlineStatusService,
    private organization: OrganizationService,
    private user: UserService,
  ) {
    window.contactService = window.cs = {
      setAllowedContacts: this.setAllowedContacts.bind(this),
      getAllowedContacts: this.getAllowedContacts.bind(this),
      addAllowedContact: this.addAllowedContact.bind(this),
      removeAllowedContact: this.removeAllowedContact.bind(this),
      fetchContactsWhereUserIsAllowed: this.fetchContactsWhereUserIsAllowed.bind(this),
      updatePresence: this.updatePresence.bind(this),
      getContactById: this.getContactById.bind(this),
    };

    this.contactsLoaded = new Promise(resolve => this.resolveContacts = resolve);
    this.setContactListenerWhenReady();
  }

  public setContactListener(): BehaviorSubject<Contact[]> {
    if (this.contactListener) {
      return this.contacts;
    }

    this.contactListener = this.fetchContactsWhereUserIsAllowed();
    this.contactListener.subject.subscribe(contacts => {
      this.personalContacts = contacts || [];
      this.mergeAndEmitContacts();
    });

    return this.contacts;
  }

  public deleteContactListener() {
    if (this.contactListener) {
      this.contactListener.unsubscribe();
      this.contacts.complete();
      this.contacts = new BehaviorSubject<Contact[]>([]);
      this.contactListener = null;
    }
    if (this.orgMemberListener) {
      this.orgMemberListener();
      this.orgMemberListener = null;
    }
    this.personalContacts = [];
    this.orgMemberContacts = [];
  }

  // Set allowed contacts in Firestore on /users/{uid}/allowed
  public async setAllowedContacts(contacts: string[]): Promise<void> {
    const uid = this.user.user.uid;
    return await this.firebase.firestore.docSet(`users/${uid}`, { allowed: contacts }, { merge: true });
  }

  // Get allowed contacts from Firestore on /users/{uid}/allowed
  public async getAllowedContacts(): Promise<string[]> {
    const uid = this.user.user.uid;
    const userDoc = await this.firebase.firestore.docGet(`users/${uid}`);
    const contacts: string[] = userDoc.exists ? (userDoc.data()?.allowed || []) : [];

    return contacts;
  }

  // Add a single contact to allowed contacts in Firestore on /users/{uid}/allowed
  public async addAllowedContact(contact: string): Promise<void> {
    const uid = this.user.user.uid;
    const existingContacts = await this.getAllowedContacts();
    const allowed =  existingContacts.indexOf(contact) === -1 ? [...existingContacts, contact] : existingContacts;
    const allowedContacts = { allowed };

    if (allowed.length === existingContacts.length) {
      return;
    }

    return await this.firebase.firestore.docSet(`users/${uid}`, allowedContacts, { merge: true });
  }

  public async removeAllowedContact(contact: string) {
    const userId = this.user.user.uid;
    const existingContacts = await this.getAllowedContacts();
    const allowedContacts = { allowed: existingContacts.filter(c => c !== contact) };
    return await this.firebase.firestore.docSet(`users/${userId}`, allowedContacts, { merge: true });
  }

  public fetchContactsWhereUserIsAllowed(): FirebaseSubjectListener<Subject<Contact[]>> {
    if (!this.user?.user?.uid) {
      throw new Error('User is not logged in');
    }

    const userId = this.user.user.uid;
    const subject = new Subject<Contact[]>();

    let previousContacts: Contact[] = [];
    const unsubscribe = this.firebase.firestore.collectionListen('users', [where('allowed', 'array-contains', userId)], (snapshots) => {
      const contacts: Contact[] = snapshots.docs.map(doc => this.mapContact(doc)).sort(this.compareContacts);

      // Prevent emitting the same contacts over and over again
      if (contacts.length === previousContacts.length && contacts.length > 0) {
        const contactsAreIdentical = contacts.map(c => this.compareContacts(c, previousContacts.find(pc => pc.id === c.id))).every(c => !c);
        if (contactsAreIdentical) {
          return;
        }
      }

      previousContacts = contacts;
      subject.next(contacts);
    });

    return createFirebaseSubjectListener(subject, unsubscribe);
  }

  // Update presence on Firestore on /users/{uid}/online
  public async updatePresence(userId?: string): Promise<void> {
    this.onlineStatus.updateOnlineStatus(userId);
  }

  public async getContactById(id: string): Promise<Contact | null> {
    try {
      const contact = await this.firebase.firestore.docGet(`users/${id}`);
      return this.mapContact(contact);
    } catch {
      return null;
    }
  }

  public compareContacts(a: Contact, b: Contact): number {
    // If contacts are currently online, they are only compared if not online
    // This prevents contacts from jumping around in the list when they go online
    if (!a.online || !b.online) {
      if (a.lastOnline < b.lastOnline) {
        return 1;
      }
      if (a.lastOnline > b.lastOnline) {
        return -1;
      }
    }

    if (a.name < b.name) {
      return -1;
    }

    if (a.name > b.name) {
      return 1;
    }

    return 0;
  }

  public async deleteContact(contactId: string): Promise<{success: true; data: any}|{success: false; error: any}> {
    const userId = this.user.user.uid;
    try {
      const data = await this.firebase.functions.call('deleteContact', {
        userId,
        contactId,
      });

      return { success: true, data };
    } catch (error) {
      this.logging.error(error);
      return { success:false, error };
    }
  }

  public getContact(id: string): Contact {
    return this.contacts.value.find(c => c.id === id);
  }

  public async getAnonymousContactName(contactId: string, conversation: Conversation): Promise<string> {
    try {

      const participant = conversation.participants.find(({ userId }) => userId === contactId);
      if (!participant) {
        return 'Guest';
      }

      if (participant.name) {
        return participant.name;
      }

    } catch (error) {
      this.logging.error('ContactsService: Error fetching name of anonymous contact', error);

      return 'Guest';
    }
  }

  public async getAnonymousContact(contactId: string, conversation: Conversation): Promise<Contact> {
    try {
      const contact = await this.getContactById(contactId);
      if (contact?.name) {
        return { ...contact, anonymous: true };
      }
    } catch (error) {
      // Fall through to default
    }

    const name = await this.getAnonymousContactName(contactId, conversation);
    const defaultData = this.getAnonymousDefaultData(contactId);
    return {
      ...defaultData,
      name,
    };
  }

  public async getDefaultConversationName(contactIds: string[]): Promise<string> {
    await this.contactsLoaded;
    const ids = contactIds.filter(id => id !== this.user.user.uid);
    const contacts = await Promise.all(ids.map(async (id) => {
      const local = this.getContact(id);
      if (local?.name) return local;
      const fetched = await this.getContactById(id);
      if (fetched?.name) return fetched;
      return local || fetched;
    }));
    const names = contacts.map(c => c?.name).filter(Boolean).sort();
    return names.join(', ');
  }

  private mapContact(contact: any): Contact {
    const data = contact.data();
    if (!data) {
      return this.getAnonymousDefaultData(contact.id);
    }

    const {online, status, lastActivity} = this.onlineStatus.getOnlineActivityFromServerData(data.activity, data.createdOn);
    return {
      id: contact.id,
      name: data.name,
      email: data.email,
      // TODO - Rename this should probably be an activity: ContactActivity object
      online,
      status,
      lastOnline: lastActivity,
      avatar: data.profilePictureUrl || '',
    };
  }

  private async setContactListenerWhenReady() {
    await this.user.isReady();
    this.setContactListener();

    // Wait for org discovery/loading to finish (covers both direct field and collection group discovery)
    while (this.organization.loading) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (this.organization.data?.id) {
      this.listenToOrgMembers();
    }
  }

  private getAnonymousDefaultData(id: string): Contact {
    return {
      id,
      name: 'Guest',
      email: '',
      online: false,
      lastOnline: 0,
      anonymous: true,
    };
  }

  private async listenToOrgMembers() {
    const orgId = this.organization.data?.id;
    if (!orgId || !this.user?.user?.uid) {
      return;
    }

    const userId = this.user.user.uid;

    // Fetch member details (email, displayName) before listening
    let memberDetails: Record<string, { email: string; displayName: string }> = {};
    try {
      const result = await this.firebase.functions.call('listMembersDetailsOfOrganization', { organizationId: orgId });
      memberDetails = result?.data?.users || result?.users || {};
    } catch (err) {
      console.error('[OrgMembers] cloud function error:', err);
    }

    this.orgMemberListener = this.firebase.firestore.collectionListen(
      `organizations/${orgId}/members`,
      [],
      async (snapshots) => {
        const memberIds = snapshots.docs
          .map(doc => doc.id)
          .filter(id => id !== userId);

        const memberContacts: Contact[] = await Promise.all(
          memberIds
            .filter(memberId => memberDetails[memberId]?.displayName || memberDetails[memberId]?.email)
            .map(async (memberId) => {
              const details = memberDetails[memberId];
              const name = details.displayName || details.email || 'Member';
              const email = details.email || '';
              const memberData = await this.fetchMemberActivity(memberId);
              const { profilePictureUrl, ...activity } = memberData;
              return {
                id: memberId,
                name,
                email,
                ...activity,
                avatar: profilePictureUrl || '',
              };
            }),
        );

        this.orgMemberContacts = memberContacts.sort(this.compareContacts);
        this.mergeAndEmitContacts();
      }
    );
  }

  private async fetchMemberActivity(memberId: string): Promise<{ online: boolean; status?: OnlineStatus; lastOnline: number; profilePictureUrl?: string }> {
    try {
      const userDoc = await this.firebase.firestore.docGet(`users/${memberId}`);
      const data = userDoc.data();
      if (data?.activity) {
        const { online, status, lastActivity } = this.onlineStatus.getOnlineActivityFromServerData(data.activity, data.createdOn);
        return { online, status, lastOnline: lastActivity, profilePictureUrl: data.profilePictureUrl };
      }
    } catch {
      // User doc may not be readable
    }
    return { online: false, lastOnline: 0 };
  }

  public async refreshOrgMemberActivity(): Promise<void> {
    if (!this.orgMemberContacts.length) return;
    const updated = await Promise.all(
      this.orgMemberContacts.map(async (contact) => {
        const activity = await this.fetchMemberActivity(contact.id);
        return { ...contact, ...activity };
      }),
    );
    this.orgMemberContacts = updated.sort(this.compareContacts);
    this.mergeAndEmitContacts();
  }

  private mergeAndEmitContacts() {
    const contactMap = new Map<string, Contact>();

    // Add personal contacts first
    for (const contact of this.personalContacts) {
      contactMap.set(contact.id, contact);
    }

    // Add org members (personal contacts take precedence)
    for (const contact of this.orgMemberContacts) {
      if (!contactMap.has(contact.id)) {
        contactMap.set(contact.id, contact);
      }
    }

    const merged = Array.from(contactMap.values()).sort(this.compareContacts);
    console.log('[OrgMembers] mergeAndEmitContacts — personal:', this.personalContacts.length, 'org:', this.orgMemberContacts.length, 'merged:', merged.length);
    this.contacts.next(merged);
    this.resolveContacts(merged);
  }
}
